import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { useContext, useEffect, useState } from 'react';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { Box, Button, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import { a11yProps, deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog';
import { LoadingPage } from '../../loading-page';
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions';
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service';
import { ICapitalGood } from '../../../../shared/services/capital-good/capital-good.model';
import { GoodForm } from '../../../presentation-components/organisation-components/good/good-form';
import { TooltipButton } from '../../../presentation-components/miscellaneous-components/tooltip-button';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { EpdManagerDialog } from '../../../presentation-components/product-components/epd-manager-dialog';
import { DefraFactorDialog } from '../../../presentation-components/product-components/defra-factor-dialog';
import { IMaterialFactor } from '../../../../shared/services/capital-good/capital-good.model';
import { ProductDocumentsPanel } from '../../../presentation-components/organisation-components/good/product-documents-panel';
import { productFootprintLabel } from '../../../presentation-components/organisation-components/good/product-catalogue-card';
import { FootprintHistoryPanel } from '../../../presentation-components/organisation-components/good/footprint-history-panel';
import { Menu, MenuItem } from '@mui/material';
import { DownloadOutlined } from '@mui/icons-material';
import { downloadBlob } from '../../../../shared/utils/download-file-utils';

export const OrganisationGoodEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  //Services
  const capitalGoodService = new CapitalGoodService()
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  //State
  const [value, setValue] = useState(0)
  const [good, setGood] = useState<ICapitalGood>();
  const [editField, setEditField] = useState<string>('')
  const [hasOpenReport, setHasOpenReport] = useState<boolean>(true)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>()
  const [errorCode, setErrorCode] = useState<number>()
  const [categories, setCategories] = useState<any>()
  const [epdDialogOpen, setEpdDialogOpen] = useState(false)
  const [defraDialogOpen, setDefraDialogOpen] = useState(false)
  const [epdRefreshKey, setEpdRefreshKey] = useState(0)
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null)
  const [isDownloadingPack, setIsDownloadingPack] = useState(false)

  // Pack filenames mirror the backend's slug so both entry points agree.
  const downloadPackHandler = async (format: 'pdf' | 'xlsx') => {
    if (!id || !good || isDownloadingPack) return
    setDownloadMenuAnchor(null)
    setIsDownloadingPack(true)
    try {
      const blob = await capitalGoodService.downloadFootprintPack(id, format)
      const safeName = (good.name || 'product').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'product'
      downloadBlob({ blob, fileName: `${safeName}-footprint-pack.${format}` })
    } catch (err) {
      console.log(err)
      userNotificationsContext.openSnackbar({ open: true, message: 'The footprint pack could not be generated. Please try again.', backgroundColor: 'text.primary' })
    } finally {
      setIsDownloadingPack(false)
    }
  }

  const applyDefraFactor = (factor: IMaterialFactor) => {
    if (!id || !good) return
    const payload = {
      name: good.name,
      description: good.description,
      modelNumber: good.modelNumber,
      goodCategoryId: good.goodCategoryId,
      typeOfGood: good.typeOfGood,
      manufacturerId: good.manufacturerId,
      capitalGoodReference: good.capitalGoodReference,
      kgCarbonEmission: factor.multiplier,
      unitType: factor.measurementUnitId,
      unitAmount: 1,
      sourceOfEmission: `DEFRA: ${factor.label}${factor.calendarYear ? ` (${factor.calendarYear})` : ''}`,
      factorSource: 'library_defra',
      isVerifiedBySupplier: false,
    }
    const form = new FormData()
    form.append('capitalGood', JSON.stringify(payload))
    capitalGoodService.update(id, form)
      .then(() => {
        getGood(id)
        userNotificationsContext.openSnackbar({ open: true, message: 'DEFRA factor applied (Medium quality)' })
      })
      .catch((err) => {
        console.log(err)
        userNotificationsContext.openSnackbar({ open: true, message: 'Could not apply the factor', backgroundColor: 'text.primary' })
      })
  }
  //Loading State
  const [isFormSubmitting, setIsFormSubitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Use Effects
  useEffect(() => {
    if (searchParams.has('tabId')) {
      const token = searchParams.get('tabId')
      if (token) {
        const tabIndex = parseInt(token)
        setValue(tabIndex)
        if (tabIndex === 1) {
          setIsLoading(false)
        }
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
    // "Save first, then attach" — a product created with the EPD source lands here with the EPD dialog open.
    if (searchParams.get('openEpd') === 'true') {
      setEpdDialogOpen(true)
      deleteQueryParamFromUrl(searchParams, 'openEpd')
    }
  }, [])

  useEffect(() => {
    if (id && value === 0) {
      getGood(id)
    }
  }, [reportPeriodContext.lastSelectedReportId, id, value])


  const getGood = (id: string) => {
    capitalGoodService.getOne(id)
      .then(response => {
        setGood(response)
        setCategories(response.goodCategory)
      })
      .catch(err => {
        console.log(err)
      })
  }

  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 0) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }

  const submitHandler = async (formData: FormData) => {
    if (id && formData) {
      if (!isFormSubmitting) {
        setIsFormSubitting(true)
        capitalGoodService.update(id, formData)
          .then(response => {
            getGood(response.id!)
            setEditField('')
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
            setIsFormSubitting(false)
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            }
            setIsFormSubitting(false)
            console.log(err)
          })
      }
    }
  }

  const deleteHandler = (id: string, isInUse: boolean) => {
    if (isInUse) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Required',
        message: `You are unable to delete this product as it has or currently is in use. Please review and update any associated emissions before attempting to delete it.`,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this ${screenLabelsContext.getLabel('product')}? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            if (!isFormSubmitting) {
              setIsFormSubitting(true)
              capitalGoodService.remove(id)
                .then(() => {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
                  navigate('/organisation/products')
                  setIsFormSubitting(false)
                })
                .catch(err => {
                  if (err.response.data.code === 1) {
                    userNotificationsContext.openDialog({
                      active: true, title: 'Action Not Allowed',
                      message: `This ${screenLabelsContext.getLabel('product')} cannot be deleted because it is currently in use, or has been used previously.`,
                      buttonCopy: 'Ok',
                      hideCancelButton: true
                    })
                  } else {
                    userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  }
                  setIsFormSubitting(false)
                  console.log(err)
                })
            }
          }
        })
    }
  }

  const deactivateHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `By deactivating this ${screenLabelsContext.getLabel('product')}, you will not be able to add any more emissions to the ${screenLabelsContext.getLabel('product')}. This action cannot be undone.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response) {
          if (!isFormSubmitting) {
            setIsFormSubitting(true)
            capitalGoodService.deactivate(id)
              .then(response => {
                getGood(id)
                userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
                setIsFormSubitting(false)
              })
              .catch(err => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                console.log(err)
                setIsFormSubitting(false)
              })
          }
        }
      })
  }

  // The footprint beside the title, worded exactly as the catalogue card words it.
  const footprintChipLabel = good ? productFootprintLabel(good) : null

  return (
    <PageOuterContainer>
      {good &&
        <>
          {good.organisationId === null ?
            <Stack direction='row' justifyContent='space-between'>
              <PageHeading
                title={good.name}
                includeBackButton={true}
                backButtonLink='/organisation/products'
                backButtonText={`Back to ${screenLabelsContext.getLabel('product', { plural: true })}`}
                hasTabs={true}
                includeActionButton={true}
                showChip={!!footprintChipLabel}
                chipColor='primary'
                chipLabel={footprintChipLabel ?? undefined}
              />
              <Stack direction='row' alignItems='center' justifyContent='flex-end' sx={{ pt: 2 }}>
                <TooltipButton
                  text={`This is a global ${screenLabelsContext.getLabel('product')} ${(!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? `` : 'provided by Gaia'} and cannot be edited`}
                  placement='left'
                />
                <Chip color='primary' variant='outlined' label={`Global ${screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })}`} />
              </Stack>
            </Stack> :
            <PageHeading
              title={good.name}
              includeBackButton={true}
              backButtonLink='/organisation/products'
              backButtonText={`Back to ${screenLabelsContext.getLabel('product', { plural: true })}`}
              hasTabs={true}
              includeActionButton={true}
              showChip={!!footprintChipLabel}
              chipColor='primary'
              chipLabel={footprintChipLabel ?? undefined}
              actionButtonIcon={IconName.HideSourceOutlined}
              actionButtonHandler={() => deactivateHandler(good.id)}
              actionButtonText='Deactivate'
              hideShowActionButton={!good.active || good.organisationId === null}
              includeSecondaryActionButton={true}
              secondaryActionButtonIcon={IconName.DeleteOutline}
              secondaryActionButtonHandler={() => deleteHandler(good.id, good.capitalGoodInUse)}
              secondaryActionButtonText='Delete'
              hideShowSecondaryActionButton={good.organisationId === null}
            />
          }
          <Stack direction='row' justifyContent='space-between' alignItems='flex-end' sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={changeTabHandler}>
              <Tab label='Details'  {...a11yProps(0)} sx={{ borderRadius: 1 }} />
              <Tab label='Emissions' {...a11yProps(1)} sx={{ borderRadius: 1 }} />
              {good.organisationId !== null && <Tab label='Documents' {...a11yProps(2)} sx={{ borderRadius: 1 }} />}
              {good.organisationId !== null && <Tab label='History' {...a11yProps(3)} sx={{ borderRadius: 1 }} />}
            </Tabs>
            {good.organisationId !== null && (
              <>
                <Button
                  id='footprint-pack-download-button'
                  variant='outlined'
                  size='small'
                  disabled={isDownloadingPack}
                  startIcon={<DownloadOutlined fontSize='small' />}
                  onClick={(event) => setDownloadMenuAnchor(event.currentTarget)}
                  aria-controls={downloadMenuAnchor ? 'footprint-pack-download-menu' : undefined}
                  aria-haspopup='true'
                  sx={{ mb: 0.75, flexShrink: 0 }}
                >
                  <Typography sx={{ pl: 0.5 }}>{isDownloadingPack ? 'Preparing pack' : 'Download footprint pack'}</Typography>
                </Button>
                <Menu
                  id='footprint-pack-download-menu'
                  anchorEl={downloadMenuAnchor}
                  open={Boolean(downloadMenuAnchor)}
                  onClose={() => setDownloadMenuAnchor(null)}
                  MenuListProps={{ 'aria-labelledby': 'footprint-pack-download-button' }}
                >
                  <MenuItem onClick={() => downloadPackHandler('pdf')}>PDF pack</MenuItem>
                  <MenuItem onClick={() => downloadPackHandler('xlsx')}>Spreadsheet (XLSX)</MenuItem>
                </Menu>
              </>
            )}
          </Stack>
          <CustomTabPanel value={value} index={0}>
            {good &&
              <GoodForm
                categories={categories}
                isEditPage={true}
                editField={editField}
                setEditField={setEditField}
                onSubmit={submitHandler}
                isSubmitting={isFormSubmitting}
                newGood={good}
                goodActive={good.active && good.organisationId !== null}
                errorMessage={errorMessage}
                errorCode={errorCode}
                globalGood={!good.organisationId}
                goodInUse={good.capitalGoodInUse}
                setErrorMessage={setErrorMessage}
                setErrorCode={setErrorCode}
                onChooseDefra={() => setDefraDialogOpen(true)}
                onManageEpd={() => setEpdDialogOpen(true)}
              />}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {!hasOpenReport &&
              <Box>
                < ClosedReportPeriodMessage openDialog={() => { setDialogOpen(true) }} />
                {dialogOpen && <StartNewReportPeriodDialog open={dialogOpen} closeHandler={() => { setDialogOpen(false) }} />}
              </Box>
            }
            {hasOpenReport &&
              <LoadingPage pageReady={!isLoading}>
                {reportPeriodContext.lastSelectedReportId && id && (
                  <Emissions
                    includeDelete={false}
                    includeEdit={false}
                    queryString={`&capitalGoodId=${id}`}
                    backLink={`/organisation/products/${id}?tabId=1`}
                    backText='Back to product'
                  />
                )}
              </LoadingPage>}
          </CustomTabPanel>
          {good.organisationId !== null && (
            <CustomTabPanel value={value} index={2}>
              <ProductDocumentsPanel
                good={good}
                canEdit={good.active}
                onManageEpd={() => setEpdDialogOpen(true)}
                epdRefreshKey={epdRefreshKey}
              />
            </CustomTabPanel>
          )}
          {good.organisationId !== null && (
            <CustomTabPanel value={value} index={3}>
              <FootprintHistoryPanel good={good} />
            </CustomTabPanel>
          )}
          {/* Dialogs sit outside the tab panels so Manage EPDs works from the Documents tab too. */}
          <EpdManagerDialog
            open={epdDialogOpen}
            onClose={() => setEpdDialogOpen(false)}
            onChanged={() => {
              if (id) getGood(id)
              setEpdRefreshKey((key) => key + 1)
            }}
            good={good}
          />
          <DefraFactorDialog open={defraDialogOpen} onClose={() => setDefraDialogOpen(false)} onSelect={applyDefraFactor} />
        </>}
    </PageOuterContainer>
  )
}